import { useEffect, useState } from "react";
import { Modal, Button, Form, Card, Spinner, Badge } from 'react-bootstrap';
import { Search, Plus, FileText } from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../api";

export default function AddProceduresModal({ show, onHide, currentProcedures, customer_cpf, onSuccess }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (searchTerm.length === 0) return;

    const searchProcedures = async (term) => {
      setIsSearching(true);
      setHasSearched(true);

      await api.get(`api/procedures/search/${encodeURIComponent(term)}`)
        .then(response => {
          setProcedures(response.data.data)
        })
        .catch(error => {
          console.error(error)
        })
        .finally(() => {
          setIsSearching(false)
        })

    };

    const timer = setTimeout(() => {
      searchProcedures(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSelectProcedure = (procedure) => {
    const isSelected = selectedProcedures.some(
      (p) => p.id === procedure.id
    );

    if (isSelected) {
      setSelectedProcedures((prev) =>
        prev.filter((p) => p.id !== procedure.id)
      );
    } else {
      setSelectedProcedures((prev) => [...prev, { ...procedure }]);
    }
  };

  const handleAddProcedures = async () => {
    const maxClinicId = currentProcedures.length > 0
      ? Math.max(...currentProcedures.map(p => p.clinic_id))
      : 0;

    const itemWithMaxClinic = currentProcedures.find(p => p.clinic_id === maxClinicId) || null;

    const proceduresToAdd = selectedProcedures.map(proc => ({
      shopper_id: 14,
      clinic_id: maxClinicId,
      procedure_id: proc.id,
      own_code: proc.own_code,
      name: proc.name,
      name_cbhpm: proc.name_cbhpm,
      cbhpm_code: proc.cbhpm_code,
      tuss_code: proc.tuss_code,
      scheduling_id: null,
      requisition_id: null,
      customer_cpf,
      quantity: proc.qtd_tag === 0 ? 1 : proc.qtd_tag,
      professional_cpf: itemWithMaxClinic ? itemWithMaxClinic.professional_cpf : null,
      setor_id: proc.sector_id,
    }))

    await api.post('api/clinic-procedures/array', proceduresToAdd)
      .then(async (response) => {
        window.dispatchEvent(new CustomEvent('refetchData'));
        
        Swal.fire({
          icon: "success",
          title: "Procedimentos adicionados com sucesso",
          toast: true,
          position: "top",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true
        });

        handleClose();
        onSuccess();
      })
      .catch(error => {
        Swal.fire({
          icon: "error",
          title: "Erro ao adicionar procedimentos",
          toast: true,
          position: "top",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true
        });

        console.error(error)
      })
  };

  const handleClose = () => {
    setHasSearched(false);
    setProcedures([]);
    setSelectedProcedures([]);
    setSearchTerm("");
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Adicionar Procedimentos</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Campo de busca */}
        <div className="mb-4">
          <div className="d-flex align-items-center border rounded px-3 py-2">
            <Search size={20} className="text-muted me-2" />
            <Form.Control
              type="text"
              placeholder="Pesquisar por nome ou código cbhpm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
              className="border-0 p-0"
              style={{ boxShadow: 'none' }}
            />
          </div>
        </div>

        {/* Listagem de procedimentos */}
        <div className="row g-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {!hasSearched && searchTerm.length < 2 && (
            <div className="text-center p-5 text-muted">
              <FileText size={48} className="mb-3" />
              <p className="fs-5">Busque procedimentos no campo acima</p>
            </div>
          )}

          {isSearching && (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted">Buscando procedimentos...</p>
            </div>
          )}

          {hasSearched && !isSearching && procedures.length > 0 && (
            <div>
              {procedures.map((proc) => {
                const isSelected = selectedProcedures.some(
                  (p) => p.id === proc.id
                );

                return (
                  <div key={proc.id} className="col-12">
                    <Card
                      className={`cursor-pointer mb-2 ${isSelected ? 'border-primary bg-light' : ''}`}
                      onClick={() => handleSelectProcedure(proc)}
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex align-items-start">
                          <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => { }}
                            className="me-3 mt-1"
                            style={{ pointerEvents: 'none' }}
                          />

                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <h6 className="fw-bold">{proc.name}</h6>
                            </div>

                            <small className="text-muted">
                              Código: {proc.cbhpm_code}
                            </small>

                            {
                              proc.is_lab && (
                                <Badge bg="info" className="ms-2" style={{ fontSize: '0.75rem' }}>
                                  Laboratório
                                </Badge>
                              )
                            }
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}

          {hasSearched && !isSearching && procedures.length === 0 && (
            <div className="col-12">
              <div className="text-center py-5">
                <Search size={48} className="text-muted mb-3" />
                <p className="text-muted">Nenhum procedimento encontrado</p>
              </div>
            </div>
          )}
        </div>

        {/* Contador de selecionados */}
        {selectedProcedures.length > 0 && (
          <div className="alert alert-info mt-3 mb-0">
            <strong>{selectedProcedures.length}</strong> procedimento(s) selecionado(s)
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex gap-2 pt-4">
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleAddProcedures}
            disabled={selectedProcedures.length === 0}
          >
            <Plus size={18} className="me-1" />
            Adicionar Procedimentos
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};